// CUSTOM:aalto-special-variants-setup-v1.1 — entire file is this customization.
// See customizations/0004-0006 for what each part is for, and the session summary for the full
// member list.
//
// Port of finnstyle's variant-picker-accordion.js. Everything is delegated from document, so it
// keeps working when the picker's DOM is replaced. Since Enterprise v2.3.0, variant-picker.js
// replaces it on every selection (it re-fetches the section and swaps the wrapper marked
// data-dynamic-variant-content), so the "State across the theme's DOM swap" part below carries
// the open/scroll/show-more state and the dots over to the new markup.

if (!window.variantPickerAccordionInit) {
  window.variantPickerAccordionInit = true;

  const ROOT = '.variant-picker-accordion';

  // ---- Collapsed-header indicator dots (finnstyle 0008) -------------------------------------
  // Built from the swatches the theme already rendered in each accordion body, so they can never
  // drift from the real ones. Variant-image swatches become tiny copies of the same image; colour
  // swatches copy the background the theme paints on the label's ::before. Sampled evenly across
  // the whole list (deterministic, so they never reshuffle).
  const buildDots = () => {
    document.querySelectorAll(`${ROOT} .variant-picker-accordion__dots`).forEach((target) => {
      const item = target.closest('.option-selector');
      const swatches = item ? item.querySelectorAll('.opt-label--swatch') : [];
      if (!swatches.length) return;

      const max = parseInt(target.dataset.dotCount, 10) || 7;
      const step = Math.max(1, Math.floor(swatches.length / max));
      const picked = [];
      for (let i = 0; i < swatches.length && picked.length < max; i += step) {
        picked.push(swatches[i]);
      }

      target.replaceChildren(
        ...picked.map((swatch) => {
          const img = swatch.querySelector('img');
          if (img) {
            const dot = document.createElement('img');
            dot.className = 'variant-picker-accordion__dot';
            dot.src = img.currentSrc || img.src;
            dot.alt = '';
            return dot;
          }
          const dot = document.createElement('span');
          dot.className = 'variant-picker-accordion__dot';
          const before = window.getComputedStyle(swatch, '::before');
          const own = window.getComputedStyle(swatch);
          dot.style.backgroundColor = before.backgroundColor !== 'rgba(0, 0, 0, 0)' ? before.backgroundColor : own.backgroundColor;
          dot.style.backgroundImage = before.backgroundImage !== 'none' ? before.backgroundImage : own.backgroundImage;
          return dot;
        })
      );
    });
  };

  // ---- Header "Name: value" sync -------------------------------------------------------------
  // The stock picker only shows the selected value for colour options, via a .js-color-text span
  // that variant-picker.js updates. The accordion shows it for every option, so keep it in sync
  // here instead. This listener is on document, so it runs after variant-picker.js's own
  // 'change' handler on the <variant-picker> element.
  document.addEventListener('change', (evt) => {
    const item = evt.target.closest?.(`${ROOT} .option-selector`);
    if (!item) return;
    const valueEl = item.querySelector('[data-accordion-value]');
    if (!valueEl) return;

    let value = '';
    if (item.dataset.selectorType === 'dropdown') {
      const text = item.querySelector('.custom-select__btn')?.textContent.trim() || '';
      const placeholder = item.closest(ROOT)?.dataset.placeholderText;
      value = text === placeholder ? '' : text;
    } else {
      value = item.querySelector('input:checked')?.value || '';
    }
    valueEl.textContent = value;
  });

  // ---- Sub-category group "Show more" (finnstyle 0004) ---------------------------------------
  // Each group grid is clamped to N rows in CSS. The toggle is only shown when a group actually
  // overflows at the current width, so it is measured once the accordion is open (a closed
  // <details> has no layout) and again on resize.
  const updateGroups = (details) => {
    details.querySelectorAll('[data-accordion-group]').forEach((group) => {
      const grid = group.querySelector('.variant-picker-accordion__group-grid');
      const button = group.querySelector('.variant-picker-accordion__show-more');
      if (!grid || !button) return;
      if (group.classList.contains('is-expanded')) {
        button.hidden = false;
        return;
      }
      button.hidden = grid.scrollHeight <= grid.clientHeight + 1;
    });

    // If the theme's open transition is still running, its inline target height was measured
    // before the buttons above were shown or hidden - retarget it so nothing is clipped.
    const panel = details.querySelector(':scope > .disclosure__panel');
    if (panel && panel.style.height && panel.style.height !== '0px' && !details.classList.contains('is-closing')) {
      panel.style.height = `${panel.scrollHeight}px`;
    }
  };

  const setExpanded = (group, expanded) => {
    const button = group.querySelector('.variant-picker-accordion__show-more');
    group.classList.toggle('is-expanded', expanded);
    if (!button) return;
    button.setAttribute('aria-expanded', String(expanded));
    button.querySelector('.variant-picker-accordion__show-more-label').hidden = expanded;
    button.querySelector('.variant-picker-accordion__show-less-label').hidden = !expanded;
  };

  // 'toggle' does not bubble, but capture-phase listening on document still receives it.
  document.addEventListener(
    'toggle',
    (evt) => {
      const details = evt.target;
      if (details.open && details.matches?.(`${ROOT} .variant-picker-accordion__details`)) {
        updateGroups(details);
      }
    },
    true
  );

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      document.querySelectorAll(`${ROOT} .variant-picker-accordion__details[open]`).forEach(updateGroups);
    }, 150);
  });

  document.addEventListener('click', (evt) => {
    const button = evt.target.closest?.(`${ROOT} .variant-picker-accordion__show-more`);
    if (!button) return;
    const group = button.closest('[data-accordion-group]');
    const expanding = !group.classList.contains('is-expanded');
    setExpanded(group, expanding);
    if (!expanding) {
      const grid = group.querySelector('.variant-picker-accordion__group-grid');
      grid.scrollTop = 0;
      group.scrollIntoView({ block: 'nearest' });
    }
  });

  // Keyboard users arrowing through a radio group can land on a swatch hidden by the clamp -
  // expand that group so the focused swatch is visible.
  document.addEventListener('focusin', (evt) => {
    const input = evt.target;
    if (!input.matches?.(`${ROOT} .variant-picker-accordion__group-grid .js-option`)) return;
    const group = input.closest('[data-accordion-group]');
    const grid = group.querySelector('.variant-picker-accordion__group-grid');
    const label = input.nextElementSibling;
    if (!label || group.classList.contains('is-expanded')) return;
    if (label.getBoundingClientRect().bottom > grid.getBoundingClientRect().bottom + 1) {
      setExpanded(group, true);
    }
  });

  // ---- Swatch-name tooltip (finnstyle 0006 + 0007) -------------------------------------------
  // Enterprise swatches have no hover tooltip of their own. One shared element on <body> is
  // positioned over the hovered swatch, so the row-cap scroll boxes and group clamps can never
  // clip it, and no transformed ancestor can become its containing block (the bug finnstyle 0006
  // fixed). It fades out where it faded in.
  let tooltip;
  let activeLabel;

  const getTooltip = () => {
    if (!tooltip) {
      tooltip = document.createElement('div');
      tooltip.className = 'variant-picker-accordion-tooltip';
      tooltip.setAttribute('aria-hidden', 'true');
      document.body.appendChild(tooltip);
    }
    return tooltip;
  };

  // v2.3.0 marks availability only with .is-unavailable on the input (server-rendered). Word it
  // the way the theme's custom-select does: sold out if the variant exists, else unavailable.
  const getStatus = (input) => {
    if (!input.classList.contains('is-unavailable')) return '';
    const strings = window.theme?.strings || {};
    return (input.dataset.variantId ? strings.noStock : strings.noVariant) || '';
  };

  const showTooltip = (label) => {
    const input = label.htmlFor ? document.getElementById(label.htmlFor) : null;
    const name = input?.value.trim();
    if (!name) return;
    const status = getStatus(input);

    const tip = getTooltip();
    tip.textContent = status ? `${name} – ${status}` : name;
    const rect = label.getBoundingClientRect();
    const half = tip.offsetWidth / 2;
    const x = Math.min(Math.max(rect.left + rect.width / 2, half + 8), window.innerWidth - half - 8);
    tip.style.left = `${x}px`;
    tip.style.top = `${rect.top}px`;
    tip.classList.add('is-visible');
    activeLabel = label;
  };

  const hideTooltip = () => {
    activeLabel = null;
    tooltip?.classList.remove('is-visible');
  };

  const SWATCH = `${ROOT} .opt-label--swatch`;

  document.addEventListener('mouseover', (evt) => {
    const label = evt.target.closest?.(SWATCH);
    if (label && label !== activeLabel) {
      hideTooltip();
      showTooltip(label);
    }
  });
  document.addEventListener('mouseout', (evt) => {
    const label = evt.target.closest?.(SWATCH);
    if (label && label === activeLabel && !label.contains(evt.relatedTarget)) hideTooltip();
  });
  document.addEventListener('focusin', (evt) => {
    if (!evt.target.matches?.(`${ROOT} .js-option:focus-visible`)) return;
    const label = evt.target.nextElementSibling;
    if (label?.matches('.opt-label--swatch')) showTooltip(label);
  });
  document.addEventListener('focusout', (evt) => {
    if (evt.target.matches?.(`${ROOT} .js-option`)) hideTooltip();
  });
  // Any scroll moves the swatch out from under a fixed tooltip - hide rather than chase it.
  document.addEventListener('scroll', hideTooltip, { capture: true, passive: true });

  // ---- Select without the browser's focus-scroll (2026-10-01 fix 2) -------------------------
  // Clicking a <label> natively focuses its radio, and the browser scrolls to wherever that radio
  // is. The theme's .visually-hidden radios are position:absolute, so inside the accordion's scroll
  // box they are not beside their swatch, and that scroll jumped the page (or the box) after
  // "Show more". Do the label's job ourselves: focus without scrolling, check the radio, and fire
  // the same bubbling 'change' the browser would, so variant-picker.js runs unchanged.
  document.addEventListener(
    'click',
    (evt) => {
      const label = evt.target.closest?.(`${ROOT} label.opt-label[for]`);
      if (!label) return;
      const input = document.getElementById(label.htmlFor);
      if (!input || input.disabled || input.type !== 'radio') return;
      evt.preventDefault();
      input.focus({ preventScroll: true });
      if (!input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    },
    true
  );

  // ---- State across the theme's DOM swap (v1.1, Enterprise v2.3.0) --------------------------
  // variant-picker.js fires 'on:variant:before-replace-element' on document just before it swaps
  // each data-dynamic-variant-content element for the freshly rendered one. The server render
  // already has the new selection and header value; carry over what only the browser knows:
  // which accordions are open, which groups are expanded (and whether their toggle is shown),
  // the dots, and each grid's scroll position. Matching is by position, which is stable because
  // the old and new markup come from the same template.
  document.addEventListener('on:variant:before-replace-element', (evt) => {
    const { toReplace, replaceWith } = evt.detail || {};
    if (!toReplace?.querySelectorAll || !replaceWith?.querySelectorAll) return;
    const ITEM = `${ROOT} .variant-picker-accordion__item`;
    const oldItems = toReplace.querySelectorAll(ITEM);
    const newItems = replaceWith.querySelectorAll(ITEM);
    if (!oldItems.length || oldItems.length !== newItems.length) return;

    hideTooltip();
    const scrollY = window.scrollY;
    const scrolls = [];
    const pair = (oldItem, newItem, selector, fn) => {
      const olds = oldItem.querySelectorAll(selector);
      const news = newItem.querySelectorAll(selector);
      olds.forEach((oldEl, i) => news[i] && fn(oldEl, news[i]));
    };

    oldItems.forEach((oldItem, i) => {
      const newItem = newItems[i];

      pair(oldItem, newItem, '.variant-picker-accordion__details', (oldEl, newEl) => {
        newEl.open = oldEl.open && !oldEl.classList.contains('is-closing');
      });

      pair(oldItem, newItem, '[data-accordion-group]', (oldEl, newEl) => {
        setExpanded(newEl, oldEl.classList.contains('is-expanded'));
        const oldButton = oldEl.querySelector('.variant-picker-accordion__show-more');
        const newButton = newEl.querySelector('.variant-picker-accordion__show-more');
        if (oldButton && newButton) newButton.hidden = oldButton.hidden;
      });

      pair(oldItem, newItem, '.variant-picker-accordion__dots', (oldEl, newEl) => {
        newEl.replaceChildren(...Array.from(oldEl.childNodes, (node) => node.cloneNode(true)));
      });

      pair(oldItem, newItem, '.variant-picker-accordion__scroll, .variant-picker-accordion__group-grid', (oldEl, newEl) => {
        scrolls.push([newEl, oldEl.scrollTop]);
      });
    });

    // scrollTop only sticks once the new markup is in the document. The swap happens
    // synchronously after this event, and the theme then focuses the clicked input (which can
    // scroll its grid or the page), all within the same task, so restore in a microtask queued
    // behind both. Microtasks run before the next paint, so nothing visibly jumps.
    queueMicrotask(() => {
      scrolls.forEach(([el, top]) => {
        el.scrollTop = top;
      });
      if (window.scrollY !== scrollY) window.scrollTo({ top: scrollY, behavior: 'instant' });
    });
  });

  // ---- Init --------------------------------------------------------------------------------
  buildDots();
  document.addEventListener('shopify:section:load', buildDots);
}
